"use client"

import * as React from "react"
import { cn } from "@/lib/utils"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"

// ============================================================================
// TypeScript Types
// ============================================================================

/**
 * Customer data structure from API
 */
interface WorkspaceCustomer {
  id: string
  name: string
  contactEmail: string | null
  contactPhone: string | null
}

/**
 * Workspace data structure from API
 */
interface Workspace {
  id: string
  name: string
  slug: string
  createdAt: string
  updatedAt: string
  memberCount: number
  userRole: string
  customer: WorkspaceCustomer | null
}

/**
 * API response structure
 */
interface WorkspacesResponse {
  data: Workspace[]
  pagination: {
    page: number
    limit: number
    total: number
    totalPages: number
    hasNext: boolean
    hasPrev: boolean
  }
}

/**
 * Component props
 */
export interface WorkspaceSwitcherProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Optional callback when workspace changes */
  onWorkspaceChange?: (workspaceId: string) => void
}

// ============================================================================
// Icons
// ============================================================================

/**
 * Workspace/building icon
 */
function WorkspaceIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="M3 21h18" />
      <path d="M9 8h1" />
      <path d="M9 12h1" />
      <path d="M9 16h1" />
      <path d="M14 8h1" />
      <path d="M14 12h1" />
      <path d="M14 16h1" />
      <path d="M6 21V7a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v14" />
    </svg>
  )
}

// ============================================================================
// Component
// ============================================================================

/**
 * WorkspaceSwitcher - Dropdown component for switching between user workspaces
 *
 * Features:
 * - Fetches user's workspaces from API
 * - Displays current workspace
 * - Allows switching between workspaces
 * - Persists selection in localStorage
 * - Shows workspace icon and name
 *
 * Usage:
 * ```tsx
 * <WorkspaceSwitcher />
 * // or with callback
 * <WorkspaceSwitcher onWorkspaceChange={(id) => console.log(id)} />
 * ```
 */
export const WorkspaceSwitcher = React.forwardRef<HTMLDivElement, WorkspaceSwitcherProps>(
  ({ className, onWorkspaceChange, ...props }, ref) => {
    const [workspaces, setWorkspaces] = React.useState<Workspace[]>([])
    const [currentWorkspaceId, setCurrentWorkspaceId] = React.useState<string>("")
    const [loading, setLoading] = React.useState(true)
    const [error, setError] = React.useState<string | null>(null)

    /**
     * Fetch workspaces from API on mount
     */
    React.useEffect(() => {
      const fetchWorkspaces = async () => {
        try {
          const response = await fetch("/api/workspaces?limit=100")

          if (!response.ok) {
            throw new Error("Failed to fetch workspaces")
          }

          const result: WorkspacesResponse = await response.json()
          setWorkspaces(result.data)

          // Set current workspace from localStorage or use first workspace
          const savedWorkspaceId = localStorage.getItem("current_workspace_id")
          if (savedWorkspaceId && result.data.some((w) => w.id === savedWorkspaceId)) {
            setCurrentWorkspaceId(savedWorkspaceId)
            // Sync cookie for server-side access
            document.cookie = `current_workspace_id=${encodeURIComponent(savedWorkspaceId)};path=/;max-age=${60 * 60 * 24 * 365};SameSite=Lax`
          } else if (result.data.length > 0) {
            // Default to first workspace
            const firstWorkspaceId = result.data[0].id
            setCurrentWorkspaceId(firstWorkspaceId)
            localStorage.setItem("current_workspace_id", firstWorkspaceId)
            document.cookie = `current_workspace_id=${encodeURIComponent(firstWorkspaceId)};path=/;max-age=${60 * 60 * 24 * 365};SameSite=Lax`
          }
        } catch (err) {
          setError(err instanceof Error ? err.message : "Failed to load workspaces")
        } finally {
          setLoading(false)
        }
      }

      fetchWorkspaces()
    }, [])

    /**
     * Handle workspace selection change
     */
    const handleWorkspaceChange = (workspaceId: string) => {
      setCurrentWorkspaceId(workspaceId)
      localStorage.setItem("current_workspace_id", workspaceId)
      document.cookie = `current_workspace_id=${encodeURIComponent(workspaceId)};path=/;max-age=${60 * 60 * 24 * 365};SameSite=Lax`

      // Call optional callback
      if (onWorkspaceChange) {
        onWorkspaceChange(workspaceId)
      }

      // Reload page to refresh data with new workspace context
      window.location.reload()
    }

    /**
     * Get current workspace display name
     */
    const currentWorkspace = workspaces.find((w) => w.id === currentWorkspaceId)

    // Don't render if no workspaces or loading
    if (loading || workspaces.length === 0) {
      return null
    }

    // Show error state
    if (error) {
      return (
        <div className={cn("text-sm text-destructive", className)}>
          {error}
        </div>
      )
    }

    /**
     * Format workspace display text with optional customer name
     */
    const formatWorkspaceDisplay = (workspace: Workspace, truncate = false) => {
      const name = truncate && workspace.name.length > 15
        ? `${workspace.name.slice(0, 15)}...`
        : workspace.name

      if (workspace.customer) {
        const customerName = truncate && workspace.customer.name.length > 12
          ? `${workspace.customer.name.slice(0, 12)}...`
          : workspace.customer.name
        return `${name} • ${customerName}`
      }
      return name
    }

    return (
      <div ref={ref} className={cn("flex items-center gap-2", className)} {...props}>
        <WorkspaceIcon className="h-4 w-4 text-muted-foreground hidden sm:inline" />
        <Select value={currentWorkspaceId} onValueChange={handleWorkspaceChange}>
          <SelectTrigger className="w-[220px] sm:w-[280px]">
            <SelectValue placeholder="Select workspace">
              {currentWorkspace ? formatWorkspaceDisplay(currentWorkspace, true) : "Select workspace"}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {workspaces.map((workspace) => (
              <SelectItem key={workspace.id} value={workspace.id}>
                <div className="flex flex-col w-full">
                  <div className="flex items-center justify-between w-full">
                    <span>{workspace.name}</span>
                    <span className="text-xs text-muted-foreground ml-2">
                      {workspace.userRole}
                    </span>
                  </div>
                  {workspace.customer && (
                    <span className="text-xs text-muted-foreground">
                      {workspace.customer.name}
                    </span>
                  )}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    )
  }
)
WorkspaceSwitcher.displayName = "WorkspaceSwitcher"
